/**
 * SelfHarmCrisisSupportDialog.js
 *
 * A blocking alert dialog shown to SLaM / 3 Borough patients immediately
 * after they submit a concerning answer to PHQ-9 question 9 (the
 * self-harm / suicide item). It mirrors the crisis-support content from
 * the companion patient email (self_harm_patient_support) so the patient
 * sees the resources on-screen at the moment of disclosure, without
 * needing to check their inbox.
 *
 * Design decisions:
 *   - Uses Radix AlertDialog (not Dialog) so clicking outside or pressing
 *     Escape cannot dismiss it. The only exit is the primary CTA button.
 *   - No close/X button — the patient must actively acknowledge the
 *     resources before proceeding.
 *   - The CTA is "Proceed to next question" (gold fill, plum label) per
 *     the design rules for service-user surfaces.
 *   - Content is static — no personalisation placeholders. The same
 *     resources appear regardless of which concerning answer was selected.
 *   - The dialog does NOT re-trigger the email; it is purely presentational.
 *     The email is fired (fire-and-forget) before the dialog opens.
 *
 * Props:
 *   isOpen    — boolean controlling visibility
 *   onProceed — callback invoked when the patient clicks the CTA;
 *               the parent (PatientFormTemplate) resumes navigation to Q10
 */

import React from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';

export default function SelfHarmCrisisSupportDialog({ isOpen, onProceed }) {
  return (
    <AlertDialog.Root open={isOpen}>
      <AlertDialog.Portal>
        {/* Overlay — matches AllFormsCompletedFeedbackModal */}
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 z-50 backdrop-blur-sm" />

        {/* Content panel — centred, scrollable, max-w-2xl */}
        <AlertDialog.Content
          className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-[95vw] sm:w-[90vw] max-w-2xl max-h-[90vh] overflow-y-auto z-50"
        >
          {/* Heading — AlertDialog requires a Title for accessibility */}
          <AlertDialog.Title className="text-lg font-semibold text-[#2A1A28] mb-4">
            Important information about support
          </AlertDialog.Title>

          {/* Body — static crisis-support content matching the email */}
          <AlertDialog.Description asChild>
            <div className="text-[#2A1A28] text-base leading-relaxed space-y-4">
              <p>
                Your response to this question has indicated that you may
                require mental health support.
              </p>

              <p>
                Please note we are unable to offer urgent / crisis mental
                health support but please contact the services below for
                further support:
              </p>

              <p className="font-bold text-base mt-6">
                Crisis information
              </p>

              <p className="italic">
                If you require urgent or crisis support:
              </p>

              <ul className="list-disc pl-5 space-y-2">
                <li>
                  If you need urgent support{' '}
                  <a
                    href="http://www.slam.nhs.uk/crisis"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#43213E] underline font-medium"
                  >
                    visit getting help in a crisis
                  </a>
                </li>
                <li>
                  Call{' '}
                  <span className="font-bold text-[#2980B9]">NHS 111</span>{' '}
                  and press 2 to access 24/7 mental health support
                </li>
                <li>
                  SHOUT provide free 24/7 anonymous support via text: simply
                  text{' '}
                  <span className="font-bold text-[#2980B9]">SHOUT</span> to{' '}
                  <span className="font-bold text-[#2980B9]">85258</span>
                </li>
                <li>
                  SLaM WhatsApp text service: message{' '}
                  <span className="font-bold text-[#2980B9]">
                    0303 330 1511
                  </span>{' '}
                  to receive fast, free and confidential support 24/7
                </li>
              </ul>

              <p className="italic">
                The 3 Boroughs clinics are a non-urgent outpatients service.
              </p>
            </div>
          </AlertDialog.Description>

          {/* Primary CTA — gold fill, plum label (service-user surface) */}
          <div className="mt-8 flex justify-end">
            <AlertDialog.Action asChild>
              <button
                onClick={onProceed}
                className="inline-flex items-center justify-center px-6 py-3 min-h-[44px] rounded-lg bg-[#E7C22F] text-[#43213E] font-semibold text-base transition-colors hover:bg-[#d4b129] focus:outline-none focus:ring-2 focus:ring-[#43213E] focus:ring-offset-2"
              >
                Proceed to next question
              </button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
